import React, { useEffect, useState } from 'react';
import TelephoneIcon from '../../../Icons/TelephoneIcon';
import { useTelephonyContext } from '../../../../../contexts/TelephonyContext';
import QuickConnectsHeader from './QuickConnectsHeader';
import { TelephonyWidgetContainer } from '../../../../../models/TelephonyWidgetContainer';
import QuickConnectsSearch from './QuickConnectsSearch';
import { DEPARTMENTS } from '../../../../../utils/constants';
import { IsB2bAgent } from '../../../../../utils/isB2bAgent';
import { filterVoiceQuickConnects } from '../../../../../utils/filterVoiceQuickConnects';
import { Avatar, Box, Grid, GridItem, IconButton, Typography } from '@coolblue-development/freddy';

export type QuickConnectsType = connect.EndpointType | typeof DEPARTMENTS;

const QuickConnectsContainer = (): JSX.Element => {
  const { voiceContact, setActiveTelephonyWidgetContainer } = useTelephonyContext();
  const [quickConnects, setQuickConnects] = useState<connect.Endpoint[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<QuickConnectsType>(DEPARTMENTS);

  useEffect(() => {
    connect.agent(agent => {
      const allQueues = agent.getAllQueueARNs();
      const defaultOutboundQueue = agent.getRoutingProfile().defaultOutboundQueue.queueARN;

      const queues = voiceContact ? [...allQueues, defaultOutboundQueue] : [defaultOutboundQueue];

      agent.getEndpoints(queues, {
        success: data => {
          setQuickConnects(filterVoiceQuickConnects(data.endpoints));
        },
        failure: () => {}
      });
    });
  }, [voiceContact]);

  const filteredQuickConnects = quickConnects.filter(
    quickConnect =>
      quickConnect.name.toLowerCase().includes(query.toLowerCase()) &&
      (filter === DEPARTMENTS
        ? [connect.EndpointType.QUEUE, connect.EndpointType.PHONE_NUMBER].includes(quickConnect.type)
        : quickConnect.type === filter)
  );

  const quickConnectsToDisplay = IsB2bAgent() ? filteredQuickConnects : quickConnects;

  const callQuickConnect = (selectedIndex: number): void => {
    connect.agent(agent => {
      const endpoint = connect.Endpoint.byPhoneNumber(quickConnectsToDisplay[selectedIndex].phoneNumber);
      agent.connect(endpoint);

      if (voiceContact) {
        voiceContact.addConnection(quickConnectsToDisplay[selectedIndex]);
      }

      setActiveTelephonyWidgetContainer(TelephonyWidgetContainer.CALL);
    });
  };

  return (
    <>
      <QuickConnectsHeader filter={filter} setFilter={setFilter} />
      {IsB2bAgent() && <QuickConnectsSearch query={query} setQuery={setQuery} />}
      <Box
        sx={{
          color: 'white',
          scrollbarWidth: 'none',
          overflow: 'auto',
          marginBottom: '-24px'
        }}
      >
        {quickConnectsToDisplay.map((quickConnect, index) => (
          <Grid
            key={index}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            alignItems="center"
            sx={{
              borderTop: '1px solid #262930',
              textAlign: 'left',
              padding: '12px',
              ':hover': { background: '#262930', borderRadius: '4px' },
              transition: 'background 0.3s ease'
            }}
          >
            <GridItem columnEnd="span 2">
              <Avatar>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#0090E3',
                    color: '#fff'
                  }}
                >
                  {quickConnect.name.charAt(0)}
                </Box>
              </Avatar>
            </GridItem>
            <GridItem columnEnd="span 7">
              <Typography color="#fff">{quickConnect.name}</Typography>
            </GridItem>
            <GridItem columnEnd="span 3" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => callQuickConnect(index)}
                data-testid={`quick-connect-${index}`}
                sx={{
                  visibility: hoveredIndex === index ? 'visible' : 'hidden',
                  marginRight: hoveredIndex === index ? '0px' : '-35px',
                  transition: 'margin-right 0.3s ease',
                  background: 'none',
                  borderRadius: '50px',
                  width: '35px',
                  height: '35px',
                  boxShadow: 'none',
                  ':hover': { svg: { fill: '#00B900' }, background: '#33373F', boxShadow: 'none' }
                }}
              >
                <TelephoneIcon />
              </IconButton>
            </GridItem>
          </Grid>
        ))}
      </Box>
    </>
  );
};

export default QuickConnectsContainer;
